/**
 * EmployeeDetail - a person as a canvas with panes floating over its edges.
 *
 * WHAT CHANGED, AND WHY.
 *
 * The page was ONE SCROLLING COLUMN: a `border-b border-gray-200
 * dark:border-gray-700` header block with the avatar and the email, then a
 * `md:grid-cols-2` of two cards whose every value was a hand-rolled
 * `<p className="text-sm font-medium text-gray-700 dark:text-gray-200 …">`
 * inside a `Field` that already knows how to render one. It had no tabs, so
 * a person's assigned matters and tasks - which the external-collaborator
 * page has always listed - were unreachable from the internal page, and the
 * two components that render them could only be opened from that other page.
 *
 * Then it grew a CONTEXT PANE holding identity, contact, availability and the
 * engagement facts - and that was one move too far: the Profile tab was left
 * as a single thin card (bio, specializations, memberships) while the facts
 * about the person lived in a pane beside it. The person's own page could not
 * be read without opening a pane. The pane is gone and the whole of it is the
 * PROFILE canvas: identity header, engagement fact tiles, contact/updated
 * meta strip, languages, professional summary. What remains as panes is what
 * is genuinely secondary - MEMBERSHIP (the teams and departments they are on),
 * ACTIVITY (the audit log) and DETAIL (the selected matter or task) - all
 * docked right, all reachable from the rail.
 *
 * `pages/EmployeeDetailPage.tsx` is gone with this change: it was a
 * five-line wrapper that re-exported this component and that nothing routed
 * to - `PrivateRoutes` mounts this file directly.
 */

import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import {
  PiPencilLight,
  PiCheckCircleLight,
  PiUsersThreeLight,
  PiClockCounterClockwiseLight,
  PiPlusLight,
  PiBriefcaseLight,
  PiClockLight,
  PiEnvelopeSimpleLight,
  PiMoneyLight,
  PiTranslateLight,
  PiFolderLight,
  PiKanbanLight,
  PiListChecksLight,
  PiWarningCircleLight,
} from 'react-icons/pi';
import { useBreadcrumbs } from '../../../contexts/BreadcrumbsContext';
import { useAppDispatch, RootState } from '../../../store';
import { showFeedbackModal } from '../../../store/actions/UserFeedbackActions';
import apis from '../../../utils/apis';
import { formatRole, formatDateTime } from '../../../utils/functions';
import { LayoutContext } from '../../../contexts/LayoutContext';
import Badge from '../../shared/TailwindComponents/Badge';
import Button from '../../shared/TailwindComponents/Button';
import Table from '../../shared/TailwindComponents/Table';
import Skeleton from '../../shared/TailwindComponents/Skeleton';
import EmptyState from '../../shared/TailwindComponents/EmptyState';
import GlassCard from '../../shared/brand/GlassCard';
import { Field, FactTile, FieldGrid, MetaTile, Well } from '../../shared/brand/Field';
import ProfileImage from '../../Header/ProfileImage';
import { normalise } from '../../shared/access/accessModel';
import DetailFrame from '../../shared/Frames/DetailFrame';
import DetailTabBar, { TabItem } from '../../shared/surface/DetailTabBar';
import DetailPageSurface from '../../shared/surface/DetailPageSurface';
import PaneRail from '../../shared/surface/PaneRail';
import PaneBody from '../../shared/surface/PaneBody';
import AuditLogPanel from '../../AuditLog/AuditLogPanel';
import { useFeatureTour } from '../../../hooks/useFeatureTour';
import { FeatureTour, peopleDetailTourSteps } from '../../shared/FeatureTour';
import { PERSON_SURFACE, PERSON_TABS, usePersonWorkspace } from './surface';
import {
  PersonProfile,
  PersonUserInfo,
  availabilityColor,
  availabilityLabel,
} from './surface/personModel';
import PersonMembershipPane from './surface/PersonMembershipPane';
import {
  AssignedResult,
  clientNameOf,
  requestAssignedMatters,
  requestAssignedProjects,
  requestAssignedTasks,
} from './surface/assignedWork';
import EmployeeCaseDetailPanel from './EmployeeCaseDetailPanel';
import EmployeeTaskDetailPanel from './EmployeeTaskDetailPanel';
import AssignTaskForm from './AssignTaskForm';
import { readErrorMessage } from '../../../utils/errorMessage';
interface EmployeeDetailParams {
  employeeId: string;
}

interface ColleagueProfileData {
  profile: PersonProfile;
  user_info: PersonUserInfo;
  success?: boolean;
}

/* ── The person's work ─────────────────────────────────────────────
   The three requests, the target they all key on and the row readings live
   in `surface/assignedWork`, where a test can pin the contract without
   rendering the page. Module-level functions, so the hook below memoises on
   a stable identity. */

interface AssignedList<T> {
  rows: T[];
  loading: boolean;
  /** Set means the request FAILED - which is not the same as an empty list. */
  error: string;
  load: () => void;
}

/**
 * One person's assigned work of one kind: the rows, and an error that is a
 * state rather than a `console.error`.
 *
 * Both handlers this replaces swallowed failures into the console, so a 4xx
 * and "nothing assigned" rendered the identical empty table - the single most
 * misleading thing the page did.
 */
const useAssignedWork = <T,>(
  uuid: string,
  email: string,
  request: (uuid: string, email: string) => Promise<AssignedResult<T>>,
  failureMessage: string
): AssignedList<T> => {
  const [rows, setRows] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const load = useCallback(() => {
    if (!uuid && !email) return;
    setLoading(true);
    setError('');
    (async () => {
      try {
        const { rows: fetched } = await request(uuid, email);
        setRows(fetched);
      } catch (err: any) {
        setRows([]);
        setError(
          readErrorMessage(err, failureMessage)
        );
      } finally {
        setLoading(false);
      }
    })();
  }, [uuid, email, request, failureMessage]);

  return { rows, loading, error, load };
};

const EmployeeDetail: React.FC = () => {
  const { t } = useTranslation();
  const { employeeId } = useParams<EmployeeDetailParams>();
  const dispatch = useAppDispatch();
  const { setBreadcrumbs } = useBreadcrumbs();
  const { setIsContentScrollable } = useContext(LayoutContext);
  const history = useHistory();
  const { subscriptionActive } = useSelector((state: RootState) => state.subscription);
  const reduxUserInfo = useSelector((state: RootState) => state.user?.userInfo);

  const [profileData, setProfileData] = useState<ColleagueProfileData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');
  const [showTaskForm, setShowTaskForm] = useState(false);

  /**
   * The workspace: pane layout, the selected matter/task and the active tab,
   * from the ONE shared hook. It owns the interlock - selecting a row raises
   * the detail pane, switching tabs clears it - which is precisely what a
   * hand-rolled `showSidePanel` per row could never do.
   */
  const {
    panels,
    api: surfaceApi,
    activeTab,
    goToTab,
    selection,
  } = usePersonWorkspace(employeeId);

  const { isRunning: isTourRunning, handleJoyrideCallback: handleTourCallback } = useFeatureTour({
    tourId: 'people-detail',
    steps: peopleDetailTourSteps,
    readyToShow: !loading && !!profileData,
    autoStartDelay: 600,
  });

  const isOwnProfile = useMemo(() => {
    if (!profileData?.user_info || !reduxUserInfo) return false;
    return profileData.user_info.id === reduxUserInfo.UserUID;
  }, [profileData?.user_info, reduxUserInfo]);

  useEffect(() => {
    setIsContentScrollable(false);
    return () => setIsContentScrollable(true);
  }, [setIsContentScrollable]);

  useEffect(() => {
    if (!employeeId) return;
    let isMounted = true;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const response = await apis.get(`/user-profile/user/uuid/${employeeId}`);
        if (!isMounted) return;
        if (response?.data?.success && response.data?.profile && response.data?.user_info) {
          setProfileData(response.data);
        } else {
          throw new Error('Invalid response format');
        }
      } catch (err: any) {
        if (!isMounted) return;
        const errorMsg = readErrorMessage(err, t('Failed to load profile'));
        setError(errorMsg);
        dispatch(showFeedbackModal({
          showModal: true,
          message: errorMsg,
          modalType: 'error',
          duration: 3000,
        }));
      } finally {
        if (isMounted) setLoading(false);
      }
    })();

    return () => { isMounted = false; };
  }, [employeeId, dispatch, t]);

  const email = profileData?.user_info?.email || '';
  /* The route param IS the person's uuid; `user_info.id` is the same value
     from the payload, and is preferred so the key always comes from the
     record the page is actually showing. */
  const personUuid = profileData?.user_info?.id || employeeId || '';
  const displayName = profileData
    ? `${profileData.user_info.first_name} ${profileData.user_info.last_name}`.trim()
    : '';

  useEffect(() => {
    setBreadcrumbs([
      { name: t('People'), href: '/people', current: false },
      { name: displayName || t('Loading'), href: `/people/${employeeId}`, current: true },
    ]);
    return () => setBreadcrumbs([]);
  }, [displayName, employeeId, setBreadcrumbs, t]);

  const matters = useAssignedWork(
    personUuid, email, requestAssignedMatters, t('Failed to load assigned matters')
  );
  const tasks = useAssignedWork(
    personUuid, email, requestAssignedTasks, t('Failed to load assigned tasks')
  );
  const projects = useAssignedWork(
    personUuid, email, requestAssignedProjects, t('Failed to load assigned projects')
  );

  const { load: loadMatters } = matters;
  const { load: loadTasks } = tasks;
  const { load: loadProjects } = projects;

  useEffect(() => {
    if (!personUuid && !email) return;
    if (activeTab === 'Matters' && matters.rows.length === 0 && !matters.error) loadMatters();
    if (activeTab === 'Tasks' && tasks.rows.length === 0 && !tasks.error) loadTasks();
    if (activeTab === 'Projects' && projects.rows.length === 0 && !projects.error) loadProjects();
    // Deliberately not depending on the row arrays: they are the guard, and
    // listing them would refetch the moment the first page arrived.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, personUuid, email, loadMatters, loadTasks, loadProjects]);

  const additionalHeaderElements = isOwnProfile ? (
    <Button variant="primary" size="small" onClick={() => history.push('/account?tab=personal')}>
      <PiPencilLight className="h-4 w-4 me-2" />
      {t('Edit Profile')}
    </Button>
  ) : undefined;

  const TAB_LABELS: Record<typeof PERSON_TABS[number], string> = {
    Profile: t('Profile'),
    Matters: t('Matters'),
    Projects: t('Projects'),
    Tasks: t('Tasks'),
  };
  const tabs: TabItem[] = PERSON_TABS.map((value) => ({ label: TAB_LABELS[value], value }));

  const breadcrumbs = [
    { name: t('People'), href: '/people', current: false },
    { name: displayName || t('Loading'), href: `/people/${employeeId}`, current: true },
  ];

  /**
   * A failed request looks like a failed request.
   *
   * `Table`'s own `emptyState` is for "there is nothing", so a list that could
   * not be fetched renders this instead - with the retry that a console error
   * never offered.
   */
  const failure = (list: AssignedList<any>, retry: () => void) => (
    <EmptyState
      icon={<PiWarningCircleLight />}
      headline={list.error}
      subtext={t('The list could not be loaded. This may be temporary.')}
      button={{ label: t('Try again'), onClick: retry }}
    />
  );

  if (loading) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} subscriptionActive={subscriptionActive} showBackButton>
        <div className="flex-1 flex flex-col min-h-0">
          <div className="flex-shrink-0 w-full">
            <DetailTabBar tabs={tabs} activeTab="Profile" onTabClick={() => {}} />
          </div>
          <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main">
            <Skeleton variant="detail" surface={PERSON_SURFACE} />
          </div>
        </div>
      </DetailFrame>
    );
  }

  if (error || !profileData) {
    return (
      <DetailFrame breadcrumbs={breadcrumbs} subscriptionActive={subscriptionActive} showBackButton>
        <div className="text-danger-600 dark:text-danger-400 p-4 text-center">
          {error || t('Failed to load profile')}
        </div>
      </DetailFrame>
    );
  }

  const { profile, user_info } = profileData;

  const initials = `${user_info.first_name?.[0] || ''}${user_info.last_name?.[0] || ''}`.toUpperCase();
  const languages = profile.languages_spoken || [];

  /* Rows are keyed on the record's UUID - what the endpoints return and what
     every other page links a matter or a task by. */
  const selectedMatter = selection?.kind === 'matter'
    ? matters.rows.find((m) => String(m.uuid || m.id) === selection.id)
    : undefined;
  const selectedTask = selection?.kind === 'task'
    ? tasks.rows.find((row) => String(row.uuid || row.id) === selection.id)
    : undefined;

  const detailTitle = selection?.kind === 'matter'
    ? selectedMatter?.title || t('Matter')
    : selection?.kind === 'task'
      ? selectedTask?.title || t('Task')
      : t('Details');

  return (
    <DetailFrame
      breadcrumbs={breadcrumbs}
      subscriptionActive={subscriptionActive}
      additionalHeaderElements={additionalHeaderElements}
      subHeaderRight={(
        <div className="flex flex-row gap-2 items-center">
          <Badge color={availabilityColor(profile.availability_status)}>
            {availabilityLabel(profile.availability_status) || t('Not Set')}
          </Badge>
          <Badge color="gray">{formatRole(user_info.role)}</Badge>
        </div>
      )}
      /* NO OVERFLOW MENU. It carried exactly two entries - "Teams &
         Departments" and "Activity log" - and both panes are `rail: true`,
         so it was a second door to two rooms already on the rail. */
      showBackButton
    >
      <div className="flex-1 flex flex-col min-h-0">
        <div data-tour-id="people-detail-header" className="flex-shrink-0 w-full">
          <DetailTabBar
            tabs={tabs}
            activeTab={activeTab}
            onTabClick={goToTab}
            actions={(
              <PaneRail
                panels={panels}
                ariaLabel={t('Panels')}
                labels="auto"
                items={PERSON_SURFACE.railItems({
                  t,
                  icons: {
                    membership: <PiUsersThreeLight />,
                    activity: <PiClockCounterClockwiseLight />,
                  },
                })}
              />
            )}
          />
        </div>

        <DetailPageSurface
          surface={PERSON_SURFACE}
          layoutApi={panels}
          panes={[
            {
              id: 'membership',
              title: t('Teams & Departments'),
              icon: <PiUsersThreeLight />,
              /* A thunk: the pane fetches BOTH org lists on mount, so a
                 person read from their Matters tab never makes those two
                 requests at all. */
              body: () => (
                <PersonMembershipPane
                  person={{ uuid: personUuid, email: user_info.email }}
                  displayName={displayName}
                />
              ),
            },
            {
              id: 'activity',
              title: t('Activity'),
              icon: <PiClockCounterClockwiseLight />,
              body: () => (
                <PaneBody>
                  <AuditLogPanel
                    entityType="User"
                    entityId={employeeId}
                    entityName={displayName}
                    onClose={() => panels.setVisible('activity', false)}
                  />
                </PaneBody>
              ),
            },
            {
              id: 'detail',
              title: detailTitle,
              body: selection?.kind === 'matter'
                ? <EmployeeCaseDetailPanel caseData={selectedMatter} />
                : <EmployeeTaskDetailPanel taskData={selectedTask} />,
            },
          ]}
        >
          {/* ── Profile ────────────────────────────────────────────────
              Everything the Context pane held, on the canvas it belongs on.
              NO EXTRA TOP INSET: the surface already insets its canvas by
              DOCK_INSET on all four sides, so a `py-8` here started the first
              card a good 30px below the top edge of the panes beside it. The
              wrapper claims the flex space and pads horizontally only. */}
          {activeTab === 'Profile' && (
            <div
              data-tour-id="people-detail-personal"
              className="details-canvas flex-1 min-h-0 overflow-y-auto custom-scrollbar-main"
            >
              <div className="mx-auto w-full max-w-6xl space-y-4 pb-6">
                {/* Identity - the header block, on the page it describes. */}
                <GlassCard padding="lg">
                  <div className="flex flex-wrap items-center gap-4">
                    <ProfileImage
                      profilePicture={normalise(profile).avatar}
                      email={user_info.email}
                      initials={initials || '??'}
                      name={displayName}
                      firstName={user_info.first_name}
                      lastName={user_info.last_name}
                      size="w-16 h-16"
                    />
                    <div className="min-w-0 flex-1">
                      <h1 className="text-2xl font-semibold text-fg-strong truncate">
                        {displayName}
                      </h1>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Badge color="gray">{formatRole(user_info.role)}</Badge>
                        <Badge color={availabilityColor(profile.availability_status)}>
                          {availabilityLabel(profile.availability_status) || t('Not Set')}
                        </Badge>
                      </div>
                    </div>
                  </div>
                </GlassCard>

                {/* Engagement - the facts someone opens a colleague for. */}
                <FieldGrid columns={3} measure="container">
                  <FactTile
                    icon={<PiMoneyLight />}
                    label={t('Hourly Rate')}
                    value={profile.hourly_rate ? `$${profile.hourly_rate}` : undefined}
                  />
                  <FactTile
                    icon={<PiBriefcaseLight />}
                    label={t('Years of Experience')}
                    value={profile.experience_years ?? undefined}
                  />
                  <FactTile
                    icon={<PiClockLight />}
                    label={t('Weekly Hours')}
                    value={profile.fixed_working_hours ?? undefined}
                  />
                </FieldGrid>

                <FieldGrid columns={2} measure="container">
                  <MetaTile
                    icon={<PiEnvelopeSimpleLight />}
                    label={t('Email')}
                    value={user_info.email}
                  />
                  <MetaTile
                    icon={<PiCheckCircleLight />}
                    label={t('Last updated')}
                    value={profile.updated_at ? formatDateTime(profile.updated_at) : undefined}
                    emptyText={t('Never')}
                  />
                </FieldGrid>

                {/* Every value goes through `Field`, which owns the label and
                    value type scale. It used to be passed a hand-rolled `<p>`
                    reimplementing exactly that, so `emptyText` never fired —
                    a missing bio rendered a literal "-" instead of a muted
                    "Not set". */}
                <GlassCard title={t('Professional Summary')} padding="lg">
                  <div className="space-y-4">
                    <Field label={t('Bio')} value={profile.bio} />
                    <Field
                      label={t('Specializations')}
                      value={profile.specializations?.join(', ')}
                    />
                    <Field
                      label={t('Professional Memberships')}
                      value={profile.professional_memberships?.join(', ')}
                    />
                    <Field label={t('Languages Spoken')}>
                      {languages.length > 0 ? (
                        <Well className="flex flex-wrap items-center gap-1.5">
                          <PiTranslateLight className="h-4 w-4 text-fg-muted flex-shrink-0" />
                          {languages.map((language) => (
                            <Badge key={language} color="purple">{language}</Badge>
                          ))}
                        </Well>
                      ) : undefined}
                    </Field>
                  </div>
                </GlassCard>
              </div>
            </div>
          )}

          {/* The work tabs carry NO horizontal inset: the canvas already has
              its gutter, and a second one left the table floating inside its
              own tab while every other list in the app meets its edges. */}
          {activeTab === 'Matters' && (
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main py-4">
              {matters.error ? failure(matters, loadMatters) : (
                <Table
                  tableId="person-matters"
                  columns={[t('Matter'), t('Client'), t('Status'), t('Created')]}
                  /* The matter shape is the platform's own - `title`, `uuid`,
                     `status_name`, `matter_clients` - not the Cosmos-era
                     `CaseName`/`case_status` the dead `/cases` route implied. */
                  data={matters.rows.map((row) => ({
                    id: String(row.uuid || row.id),
                    [t('Matter')]: row.title || t('Untitled matter'),
                    [t('Client')]: clientNameOf(row) || '-',
                    [t('Status')]: row.status_name || row.stage_name || row.stage || '-',
                    [t('Created')]: row.created_at ? formatDateTime(row.created_at) : '-',
                  }))}
                  isLoading={matters.loading && matters.rows.length === 0}
                  onRowClick={(row) => surfaceApi.select({ kind: 'matter', id: String(row.id), mode: 'edit' })}
                  emptyState={{
                    icon: <PiFolderLight />,
                    headline: t('No matters assigned'),
                    subtext: t('Matters this person is assigned to or collaborates on appear here.'),
                  }}
                />
              )}
            </div>
          )}

          {activeTab === 'Projects' && (
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main py-4">
              {projects.error ? failure(projects, loadProjects) : (
                <Table
                  tableId="person-projects"
                  columns={[t('Project'), t('Role'), t('Status'), t('Due')]}
                  data={projects.rows.map((row) => ({
                    id: String(row.uuid || row.id),
                    [t('Project')]: row.title || t('Untitled project'),
                    /* The endpoint already says which side of the project
                       this person is on, so the badge costs no second call. */
                    [t('Role')]: row.is_owner
                      ? <Badge color="indigo">{t('Owner')}</Badge>
                      : row.is_collaborator
                        ? <Badge color="gray">{t('Member')}</Badge>
                        : <span className="text-fg-muted text-xs">—</span>,
                    [t('Status')]: row.status ? t(row.status) : (row.stage ? t(row.stage) : '-'),
                    [t('Due')]: row.due_date ? formatDateTime(row.due_date) : '-',
                  }))}
                  isLoading={projects.loading && projects.rows.length === 0}
                  /* A project has its own page - and unlike a matter or a
                     task, nothing here edits one, so there is no detail-pane
                     body to open it into. */
                  onRowClick={(row) => history.push(`/projects/${row.id}`)}
                  emptyState={{
                    icon: <PiKanbanLight />,
                    headline: t('No projects assigned'),
                    subtext: t('Projects this person owns or collaborates on appear here.'),
                  }}
                />
              )}
            </div>
          )}

          {activeTab === 'Tasks' && (
            <div className="flex-1 min-h-0 overflow-y-auto custom-scrollbar-main py-4 space-y-4">
              {showTaskForm ? (
                <div className="px-3">
                  <GlassCard title={t('Assign Task')} padding="lg">
                    <AssignTaskForm
                      employeeEmail={email}
                      onCancel={() => setShowTaskForm(false)}
                      onAdd={() => { setShowTaskForm(false); loadTasks(); }}
                    />
                  </GlassCard>
                </div>
              ) : (
                <>
                  <div className="flex justify-end px-3">
                    <Button
                      variant="primary"
                      size="small"
                      onClick={() => setShowTaskForm(true)}
                      disabled={!subscriptionActive}
                      icon={<PiPlusLight className="h-4 w-4" />}
                    >
                      {t('Assign Task')}
                    </Button>
                  </div>
                  {tasks.error ? failure(tasks, loadTasks) : (
                    <Table
                      tableId="person-tasks"
                      columns={[t('Task'), t('Matter'), t('Status'), t('Due')]}
                      /* `_task_to_dict`'s shape: uuid, title, status, due_date
                         and the matter it hangs off. */
                      data={tasks.rows.map((row) => ({
                        id: String(row.uuid || row.id),
                        [t('Task')]: row.title || t('Untitled Task'),
                        [t('Matter')]: row.matter_name || row.matter?.title || '-',
                        [t('Status')]: row.status ? t(row.status) : '-',
                        [t('Due')]: row.due_date ? formatDateTime(row.due_date) : '-',
                      }))}
                      isLoading={tasks.loading && tasks.rows.length === 0}
                      onRowClick={(row) => surfaceApi.select({ kind: 'task', id: String(row.id), mode: 'edit' })}
                      emptyState={{
                        icon: <PiListChecksLight />,
                        headline: t('No tasks assigned'),
                        subtext: t('Assign one to put it on this person’s list.'),
                        button: subscriptionActive ? {
                          label: t('Assign Task'),
                          onClick: () => setShowTaskForm(true),
                        } : undefined,
                      }}
                    />
                  )}
                </>
              )}
            </div>
          )}
        </DetailPageSurface>
      </div>

      <FeatureTour
        steps={peopleDetailTourSteps}
        run={isTourRunning}
        onCallback={handleTourCallback}
        continuous
        showProgress
        showSkipButton
        disableOverlay
      />
    </DetailFrame>
  );
};

export default EmployeeDetail;
